import * as React from 'react';
import classNames from 'classnames';
import { PrevLocationProps, ResourceLocationWithPrev } from '@/api/product/types';
import { Area } from '@/generated/data.schemas';
import translations from '@/storybook/dummyData/translations';
import { MediaSet } from '@/types/media';
import { Image } from '../Image';
import { Link } from '../Link';
import { LoadingBar } from '../LoadingBar';
import { Price } from '../Price';
import styles from './ProductCard.module.scss';

export interface ProductCardProps {
	readonly isSkeleton?: boolean;
	readonly lazyLoading?: boolean;
	readonly responsiveSizesSettings?: string;
	readonly product?: Product;
	readonly isPricesLoading?: boolean;
	readonly isPricesError?: boolean;
	readonly prevLocation?: PrevLocationProps;
}

export interface Product {
	readonly id: string;
	readonly itemNo?: string | null;
	readonly name: string;
	readonly brand?: string;
	readonly url?: string | null;
	readonly image: MediaSet;
	readonly price?: number; // from DIH
}

/**
 * Generic ProductCard component, used on PLP, CMS product block etc.
 */
export const ProductCard: React.FunctionComponent<ProductCardProps> = ({
	isSkeleton,
	lazyLoading,
	responsiveSizesSettings,
	product,
	isPricesLoading,
	isPricesError,
	prevLocation,
}) => {
	// pass prevLocation on to make a CSR in back btn on PDP breadcrumb to make browser back work
	const route: ResourceLocationWithPrev = {
		area: Area.Product,
		productMasterId: product?.id,
		externalRoute: product?.url ?? '',
		prevLocation: prevLocation ? prevLocation : undefined,
	};

	const renderContent = (): React.ReactElement => {
		if (isSkeleton) {
			return (
				<>
					<LoadingBar width={'50%'} />
					<LoadingBar className={'u-mt-xs'} />
					<LoadingBar className={'u-mt-xs'} width={'75%'} />
					<LoadingBar className={'u-mt-xs'} width={'25%'} />
				</>
			);
		}

		return (
			<>
				{product?.name && <p className={classNames(styles.name, 'u-ellipsis-2-lines')}>{product.name}</p>}
				<p className={classNames(styles.itemNr, 'u-ellipsis')}>
					{translations.shared.itemNo} {product?.itemNo}
				</p>

				<Price
					className={styles.priceWrapper}
					price={product?.price}
					size={'md'}
					inline
					isLoading={isPricesLoading}
					isError={isPricesError}
				/>
			</>
		);
	};

	return (
		<article
			className={classNames(styles.wrapper, {
				[styles.isSkeleton]: isSkeleton,
			})}
		>
			<div className={styles.imgWrapper}>
				{product?.image && (
					<Image
						className={styles.img}
						size180={product.image.size180}
						size320={product.image.size320}
						size480={product.image.size480}
						size720={product.image.size720}
						size960={product.image.size960}
						size1280={product.image.size1280}
						size1920={product.image.size1920}
						responsiveSizesSettings={responsiveSizesSettings || '300px'}
						altText={product.name ?? product.id ?? ''}
						lazyLoading={lazyLoading}
					/>
				)}
			</div>
			<Link className={styles.productPrimaryAction} to={product?.url ?? ''} state={route}>
				{renderContent()}
			</Link>
		</article>
	);
};
